import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { productsAPI } from '../services/api';
import ProductCard from './ProductCard';
import { Button } from './ui/button';
import { Sparkles, ArrowRight } from 'lucide-react';

const NewProducts = () => {
  const { t, currentLanguage } = useLanguage();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchNewProducts = async () => {
      try {
        setLoading(true);
        const newProducts = await productsAPI.getNew(8);
        setProducts(newProducts);
      } catch (err) {
        console.error('Error fetching new products:', err);
        setError('Failed to load new products');
      } finally {
        setLoading(false);
      }
    };
    fetchNewProducts();
  }, []);

  if (loading) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('new_products_title')}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="animate-pulse">
                <div className="bg-capsule-bg rounded-3xl h-64 mb-4"></div>
                <div className="h-4 bg-capsule-bg rounded-xl mb-2"></div>
                <div className="h-4 bg-capsule-bg rounded-xl w-3/4"></div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center">
            <p className="text-capsule-coral">{error}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-white relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-40 h-40 rounded-full bg-gradient-to-br from-capsule-teal/5 to-capsule-aqua/5 blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-32 h-32 rounded-full bg-gradient-to-br from-capsule-coral/5 to-capsule-salmon/5 blur-2xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 relative">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-capsule-teal/10 border border-capsule-teal/20 px-4 py-2 rounded-full mb-4">
            <Sparkles className="w-4 h-4 text-capsule-teal" />
            <span className="text-capsule-teal text-sm font-semibold">
              {currentLanguage === 'es' ? 'RECIÉN LLEGADOS' : currentLanguage === 'ru' ? 'НОВИНКИ' : 'NEW ARRIVALS'}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t('new_products_title')}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {t('new_products_subtitle')}
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-12">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link to="/products">
            <Button 
              className="bg-gradient-to-r from-capsule-teal to-capsule-aqua hover:from-capsule-teal-dark hover:to-capsule-teal text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 shadow-teal-glow hover:shadow-lg hover:scale-105 inline-flex items-center gap-2"
              data-testid="view-all-products-btn"
            >
              {t('view_all')}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default NewProducts;
